import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { format, addMinutes, endOfDay, parse, startOfDay } from 'date-fns';
import { useAuth } from '../../context/AuthContext';
import { getMeeting, listMeetings, upsertMeeting } from '../../api/meetings/meetings';
import MeetingCancelConfirmDialog from '../components/cancel/MeetingCancelConfirmDialog';
import MeetingDetailsPageLayout from '../components/MeetingDetailsPageLayout';
import MeetingRescheduleDialog from '../components/reschedule/MeetingRescheduleDialog';
import MeetingRescheduleConfirmDialog from '../components/reschedule/confirm/MeetingRescheduleConfirmDialog';
import { FormSkeleton } from '../../components/loading/skeletons';
import '../styles/MeetingDetailsPage.css';

const DEFAULT_DURATION_MINUTES = 60;

const asDate = (value) => {
  if (!value) return null;
  try {
    if (value.toDate) return value.toDate();
    if (value instanceof Date) return value;
    return new Date(value);
  } catch {
    return null;
  }
};

const MeetingDetailsPage = () => {
  const { meetingId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { companyData } = useAuth();
  const fromArchive = Boolean(location.state?.fromArchive || searchParams.get('archived') === '1');

  const [meeting, setMeeting] = useState(location.state?.meeting ?? null);
  const [loading, setLoading] = useState(!location.state?.meeting);
  const [error, setError] = useState(null);
  const [cancelling, setCancelling] = useState(false);
  const [isCancelDialogOpen, setIsCancelDialogOpen] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [cancelReasonError, setCancelReasonError] = useState('');
  const [addingClient, setAddingClient] = useState(false);
  const [addClientError, setAddClientError] = useState(null);
  const [rescheduling, setRescheduling] = useState(false);
  const [rescheduleError, setRescheduleError] = useState('');
  const [isRescheduleDialogOpen, setIsRescheduleDialogOpen] = useState(false);
  const [isRescheduleConfirmOpen, setIsRescheduleConfirmOpen] = useState(false);
  const [rescheduleReason, setRescheduleReason] = useState('');
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTime, setSelectedTime] = useState(null);
  const [bookedTimes, setBookedTimes] = useState([]);
  const [isMobile, setIsMobile] = useState(typeof window !== 'undefined' && window.innerWidth < 600);

  useEffect(() => {
    const mql = window.matchMedia('(max-width: 600px)');
    const handle = () => setIsMobile(mql.matches);
    handle();
    mql.addEventListener('change', handle);
    return () => mql.removeEventListener('change', handle);
  }, []);

  useEffect(() => {
    if (location.state?.meeting) {
      setMeeting(location.state.meeting);
      setLoading(false);
      setError(null);
      return;
    }

    if (!companyData?.id || !meetingId) {
      return;
    }

    let cancelled = false;

    const loadMeeting = async () => {
      setLoading(true);
      setError(null);
      try {
        const meetingRow = await getMeeting({ companyId: companyData.id, id: meetingId });
        if (cancelled) return;
        if (!meetingRow) {
          setError(fromArchive ? 'Archived meeting not found.' : 'Meeting not found.');
          setMeeting(null);
        } else if (fromArchive && !meetingRow.archived) {
          setError('Archived meeting not found.');
          setMeeting(null);
        } else {
          setMeeting(meetingRow);
        }
      } catch (err) {
        if (!cancelled) {
          console.error('Error fetching meeting:', err);
          setError('Failed to load meeting.');
          setMeeting(null);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    loadMeeting();
    return () => {
      cancelled = true;
    };
  }, [companyData?.id, meetingId, location.state?.meeting, fromArchive]);

  useEffect(() => {
    const start = asDate(meeting?.dateTime);
    if (!start) {
      setSelectedDate(null);
      setSelectedTime(null);
      return;
    }

    const nextDate = new Date(start);
    nextDate.setHours(0, 0, 0, 0);
    setSelectedDate(nextDate);
    setSelectedTime(start);
    setRescheduleError('');
  }, [meeting?.id, meeting?.dateTime]);

  const handleOpenCancelDialog = () => {
    setCancelReason('');
    setCancelReasonError('');
    setIsCancelDialogOpen(true);
  };

  const handleCloseCancelDialog = () => {
    if (cancelling) return;
    setCancelReason('');
    setCancelReasonError('');
    setIsCancelDialogOpen(false);
  };

  const handleCancelReasonChange = (event) => {
    setCancelReason(event.target.value);
    if (cancelReasonError) {
      setCancelReasonError('');
    }
  };

  const handleCancelMeeting = async () => {
    if (!companyData?.id || !meeting || !meeting.id) return;
    const trimmedCancelReason = cancelReason.trim();
    if (!trimmedCancelReason) {
      setCancelReasonError('Please add a cancellation reason.');
      return;
    }

    setCancelling(true);
    try {
      const now = new Date().toISOString();
      const updated = await upsertMeeting({
        companyId: companyData.id,
        id: meeting.id,
        meeting: {
          ...meeting,
          status: 'cancelled',
          cancelReason: trimmedCancelReason,
          cancelledAt: now,
          archivedAt: now,
          archived: true,
          originalMeetingId: meeting.id,
        },
      });
      setMeeting(updated);
      setIsCancelDialogOpen(false);
      setCancelReason('');
      setCancelReasonError('');
      handleBack();
    } catch (err) {
      console.error('Error cancelling meeting:', err);
      setError('Failed to cancel meeting. Please try again.');
    } finally {
      setCancelling(false);
    }
  };

  const handleBack = () => navigate(-1);

  const handleAddClient = async (client) => {
    if (!companyData?.id || fromArchive || !meeting || !meeting.id) return;
    const name = (client.name || '').trim();
    const email = (client.email || '').trim();
    if (!name || !email) return;
    setAddClientError(null);
    setAddingClient(true);
    try {
      const existing = meeting.additionalClients || [];
      const updated = await upsertMeeting({
        companyId: companyData.id,
        id: meeting.id,
        meeting: {
          ...meeting,
          additionalClients: [...existing, { name, email }],
        },
      });
      setMeeting(updated);
      setAddClientError(null);
    } catch (err) {
      console.error('Error adding client:', err);
      setAddClientError('Failed to add client. Please try again.');
    } finally {
      setAddingClient(false);
    }
  };

  const rescheduleDuration =
    typeof meeting?.duration === 'number' && meeting.duration > 0
      ? meeting.duration
      : DEFAULT_DURATION_MINUTES;
  const currentMeetingStart = asDate(meeting?.dateTime);
  const currentMeetingEnd = currentMeetingStart
    ? addMinutes(currentMeetingStart, rescheduleDuration)
    : null;

  const rescheduleTimeSlots = useMemo(() => {
    if (!selectedDate) return [];

    const baseDate = new Date(selectedDate);
    const dayKey = baseDate
      .toLocaleDateString('en-US', { weekday: 'long' })
      .toLowerCase();

    const companyHoursForDay = companyData?.settings?.companyHours?.[dayKey] || null;

    let openTime;
    let closeTime;

    if (companyHoursForDay?.isOpen && companyHoursForDay.open && companyHoursForDay.close) {
      try {
        openTime = parse(companyHoursForDay.open.toLowerCase(), 'h:mm aa', baseDate);
        closeTime = parse(companyHoursForDay.close.toLowerCase(), 'h:mm aa', baseDate);
      } catch {
        openTime = new Date(baseDate);
        openTime.setHours(9, 0, 0, 0);
        closeTime = new Date(baseDate);
        closeTime.setHours(17, 0, 0, 0);
      }
    } else {
      openTime = new Date(baseDate);
      openTime.setHours(9, 0, 0, 0);
      closeTime = new Date(baseDate);
      closeTime.setHours(17, 0, 0, 0);
    }

    const slots = [];
    let current = new Date(openTime);
    while (current < closeTime) {
      slots.push(new Date(current));
      current = addMinutes(current, 15);
    }

    return slots.filter((slot) => {
      const slotStart = slot.getTime();
      const slotEnd = addMinutes(slot, rescheduleDuration).getTime();

      if (
        currentMeetingStart &&
        currentMeetingEnd &&
        slotStart < currentMeetingEnd.getTime() &&
        slotEnd > currentMeetingStart.getTime()
      ) {
        return false;
      }

      if (!bookedTimes.length) {
        return true;
      }

      const overlaps = bookedTimes.some(
        (entry) => slotStart < entry.end && slotEnd > entry.start
      );

      return !overlaps;
    });
  }, [bookedTimes, companyData, currentMeetingEnd, currentMeetingStart, rescheduleDuration, selectedDate]);

  useEffect(() => {
    if (fromArchive || !companyData?.id || !selectedDate) {
      setBookedTimes([]);
      return;
    }

    const loadMeetingsForDay = async () => {
      try {
        const dayStart = startOfDay(selectedDate);
        const dayEnd = endOfDay(selectedDate);
        const dayMeetings = await listMeetings({
          companyId: companyData.id,
          from: dayStart,
          to: dayEnd,
          archived: false,
        });

        const intervals = dayMeetings
          .map((data) => {
            if (data.id === meeting?.id) return null;
            if (!data.dateTime) return null;

            try {
              const dateTime = asDate(data.dateTime);
              if (!dateTime || Number.isNaN(dateTime.getTime())) return null;
              const status = String(data.status || '').toLowerCase();
              if (status === 'cancelled') return null;

              const duration =
                typeof data.duration === 'number' && data.duration > 0
                  ? data.duration
                  : DEFAULT_DURATION_MINUTES;

              return {
                start: dateTime.getTime(),
                end: addMinutes(dateTime, duration).getTime(),
              };
            } catch {
              return null;
            }
          })
          .filter((entry) => entry != null);

        setBookedTimes(intervals);
      } catch {
        setBookedTimes([]);
      }
    };

    loadMeetingsForDay();
  }, [companyData?.id, fromArchive, meeting?.id, selectedDate]);

  const handleRescheduleDateChange = (event) => {
    const value = event.target.value;
    setRescheduleError('');

    if (!value) {
      setSelectedDate(null);
      setSelectedTime(null);
      return;
    }

    const nextDate = new Date(`${value}T00:00:00`);
    setSelectedDate(nextDate);
    setSelectedTime(null);
  };

  const handleRescheduleTimeSelect = (time) => {
    setSelectedTime(time);
    setRescheduleError('');
  };

  const handleRescheduleReasonChange = (event) => {
    setRescheduleReason(event.target.value);
  };

  const handleOpenRescheduleDialog = () => {
    const start = asDate(meeting?.dateTime);
    if (start) {
      const nextDate = new Date(start);
      nextDate.setHours(0, 0, 0, 0);
      setSelectedDate(nextDate);
      setSelectedTime(null);
    }
    setRescheduleReason('');
    setRescheduleError('');
    setIsRescheduleConfirmOpen(false);
    setIsRescheduleDialogOpen(true);
  };

  const handleCloseRescheduleDialog = () => {
    if (rescheduling) return;
    setRescheduleReason('');
    setRescheduleError('');
    setIsRescheduleConfirmOpen(false);
    setIsRescheduleDialogOpen(false);
  };

  const validateRescheduleSelection = () => {
    if (!selectedDate || !selectedTime) {
      setRescheduleError('Choose a new date and time slot first.');
      return false;
    }

    const currentStart = asDate(meeting.dateTime);
    const currentEnd = currentStart ? addMinutes(currentStart, rescheduleDuration) : null;
    const selectedEnd = addMinutes(selectedTime, rescheduleDuration);

    if (
      currentStart &&
      currentEnd &&
      selectedTime.getTime() < currentEnd.getTime() &&
      selectedEnd.getTime() > currentStart.getTime()
    ) {
      setRescheduleError('Please choose a different time slot.');
      return false;
    }

    setRescheduleError('');
    return true;
  };

  const handleRescheduleMeeting = () => {
    if (!companyData?.id || fromArchive || !meeting?.id) return;
    if (!validateRescheduleSelection()) return;
    setIsRescheduleConfirmOpen(true);
  };

  const handleConfirmRescheduleMeeting = async () => {
    if (!companyData?.id || fromArchive || !meeting?.id) return;
    if (!validateRescheduleSelection()) {
      setIsRescheduleConfirmOpen(false);
      return;
    }

    setRescheduling(true);
    setRescheduleError('');

    try {
      const newDateTime = new Date(selectedTime);
      const newEndTime = addMinutes(newDateTime, rescheduleDuration);
      const trimmedReason = rescheduleReason.trim();
      const dayStart = startOfDay(newDateTime);
      const dayEnd = endOfDay(newDateTime);
      const existingMeetings = await listMeetings({
        companyId: companyData.id,
        from: dayStart,
        to: dayEnd,
        archived: false,
      });

      const hasConflict = existingMeetings.some((data) => {
        if (data.id === meeting.id) return false;
        if (!data.dateTime) return false;
        if (String(data.status || '').toLowerCase() === 'cancelled') return false;

        try {
          const existingStart = asDate(data.dateTime);
          if (!existingStart || Number.isNaN(existingStart.getTime())) return false;

          const existingDuration =
            typeof data.duration === 'number' && data.duration > 0
              ? data.duration
              : DEFAULT_DURATION_MINUTES;
          const existingEnd = addMinutes(existingStart, existingDuration);

          return (
            newDateTime.getTime() < existingEnd.getTime() &&
            newEndTime.getTime() > existingStart.getTime()
          );
        } catch {
          return false;
        }
      });

      if (hasConflict) {
        setRescheduleError('That time slot is no longer available. Please choose another.');
        return;
      }

      const refreshedMeeting = await upsertMeeting({
        companyId: companyData.id,
        id: meeting.id,
        meeting: {
          ...meeting,
          dateTime: newDateTime,
          endTime: newEndTime,
          originalDateTime: meeting.originalDateTime || meeting.dateTime || null,
          rescheduledDateTime: newDateTime,
          rescheduledAt: new Date().toISOString(),
          rescheduleReason: trimmedReason,
          rescheduleHistory: [
            ...(Array.isArray(meeting.rescheduleHistory) ? meeting.rescheduleHistory : []),
            {
              from: meeting.dateTime || null,
              to: newDateTime,
              at: new Date().toISOString(),
              reason: trimmedReason,
            },
          ],
        },
      });
      setMeeting(refreshedMeeting);
      setIsRescheduleConfirmOpen(false);
      setRescheduleReason('');
      setIsRescheduleDialogOpen(false);
    } catch (err) {
      console.error('Error rescheduling meeting:', err);
      setRescheduleError('Failed to reschedule meeting. Please try again.');
    } finally {
      setRescheduling(false);
    }
  };

  if (loading) {
    return (
      <div className="meeting-details-page">
        <FormSkeleton fields={6} label="Loading meeting" />
      </div>
    );
  }

  if (error || !meeting) {
    return (
      <div className="meeting-details-page">
        <div className="meeting-details-page__main">
          <button type="button" className="meeting-details-page__back-link" onClick={handleBack}>
            ← Back
          </button>
          <div className="meeting-details-page__error">
            <p>{error || 'Meeting not found.'}</p>
            <button type="button" className="meeting-details-page__error-dismiss" onClick={handleBack}>
              Go back
            </button>
          </div>
        </div>
      </div>
    );
  }

  const dateTime = meeting.dateTime;
  const endTime = meeting.endTime;
  const duration = meeting.duration;
  const startDate = asDate(dateTime);
  const endDate =
    startDate && typeof duration === 'number' && duration > 0
      ? addMinutes(startDate, duration)
      : asDate(endTime);

  const formatSafe = (date, fmt) => {
    if (!date) return 'N/A';
    try {
      return format(date, fmt);
    } catch {
      return 'N/A';
    }
  };

  const meetingForLayout = {
    ...meeting,
    startDate,
    endDate,
  };

  const selectedRescheduleDate = selectedDate ? format(selectedDate, 'yyyy-MM-dd') : '';

  return (
    <div className="meeting-details-page">
      <div className="meeting-details-page__main">
        <button type="button" className="meeting-details-page__back-link" onClick={handleBack}>
          ← Back
        </button>
        <MeetingDetailsPageLayout
          meeting={meetingForLayout}
          formatSafe={formatSafe}
          onCancel={fromArchive ? undefined : (cancelling ? undefined : handleOpenCancelDialog)}
          onOpenReschedule={fromArchive ? undefined : handleOpenRescheduleDialog}
          onAddClient={fromArchive ? undefined : handleAddClient}
          addingClient={addingClient}
          addClientError={addClientError}
          allowAddClient={!fromArchive && !!meeting?.id && (meeting?.status || '').toLowerCase() !== 'cancelled'}
        />
        {!fromArchive && (
          <MeetingRescheduleDialog
            open={isRescheduleDialogOpen}
            onClose={handleCloseRescheduleDialog}
            currentStartDate={startDate}
            selectedDateValue={selectedRescheduleDate}
            selectedTime={selectedTime}
            timeSlots={rescheduleTimeSlots}
            onDateChange={handleRescheduleDateChange}
            onTimeSelect={handleRescheduleTimeSelect}
            reason={rescheduleReason}
            onReasonChange={handleRescheduleReasonChange}
            onSubmit={handleRescheduleMeeting}
            isMobile={isMobile}
            rescheduling={rescheduling}
            error={rescheduleError}
          />
        )}
        {!fromArchive && (
          <MeetingCancelConfirmDialog
            open={isCancelDialogOpen}
            onClose={handleCloseCancelDialog}
            onConfirm={handleCancelMeeting}
            meetingTitle={meeting?.title}
            currentStartDate={startDate}
            reason={cancelReason}
            onReasonChange={handleCancelReasonChange}
            reasonError={cancelReasonError}
            submitting={cancelling}
          />
        )}
        {!fromArchive && (
          <MeetingRescheduleConfirmDialog
            open={isRescheduleConfirmOpen}
            onClose={() => setIsRescheduleConfirmOpen(false)}
            onConfirm={handleConfirmRescheduleMeeting}
            currentStartDate={startDate}
            nextStartDate={selectedTime}
            reason={rescheduleReason}
            submitting={rescheduling}
          />
        )}
      </div>
    </div>
  );
};

export default MeetingDetailsPage;
